import React, { useState, useCallback } from "react";
import { isEntityOn, SECURITY_SENSOR_CLASSES } from "./haConstants";
import { entityIcon } from "./haEntities";

/* ══ SMART HOME SECURITY ══════════════════════════════════
   Consolidated security panel:
   - Lock status overview with lock/unlock controls
   - Alarm control panel state + arm/disarm
   - Security-relevant binary sensors (door, window, motion) */

const ALARM_STATES = {
  disarmed:       { label: "Disarmed",    labelAr: "معطل",      color: "var(--green,#16a34a)", bg: "rgba(22,163,74,.08)" },
  armed_home:     { label: "Armed Home",  labelAr: "مسلح (منزل)", color: "var(--gold)",          bg: "var(--gold-bg)" },
  armed_away:     { label: "Armed Away",  labelAr: "مسلح (خارج)", color: "var(--gold)",          bg: "var(--gold-bg)" },
  armed_night:    { label: "Armed Night", labelAr: "مسلح (ليلي)", color: "var(--gold)",          bg: "var(--gold-bg)" },
  armed_vacation: { label: "Armed Vac.",  labelAr: "مسلح (إجازة)",color: "var(--gold)",          bg: "var(--gold-bg)" },
  triggered:      { label: "TRIGGERED",   labelAr: "منطلق!",     color: "var(--red,#ef4444)",   bg: "rgba(239,68,68,.08)" },
  pending:        { label: "Pending",     labelAr: "قيد التسليح", color: "var(--g500)",          bg: "var(--g50)" },
  arming:         { label: "Arming",      labelAr: "جارٍ التسليح",color: "var(--g500)",          bg: "var(--g50)" },
};

function SmartHomeSecurity({ entities, callService, canControl, isRTL }) {
  const locks = entities.filter(e => e.domain === "lock");
  const alarms = entities.filter(e => e.domain === "alarm_control_panel");
  const secSensors = entities.filter(e =>
    e.domain === "binary_sensor" && SECURITY_SENSOR_CLASSES.has(e.attributes?.device_class)
  );

  return (
    <div className="sh-security">
      {/* Alarm panels */}
      {alarms.map(alarm => (
        <AlarmCard key={alarm.entity_id} entity={alarm} callService={callService} canControl={canControl} isRTL={isRTL} />
      ))}

      {/* Locks */}
      {locks.length > 0 && (
        <div className="sh-sec-section">
          <h3 className="sh-section-title">{isRTL ? "الأقفال" : "Locks"}</h3>
          <div className="sh-sec-grid">
            {locks.map(lock => (
              <LockCard key={lock.entity_id} entity={lock} callService={callService} canControl={canControl} isRTL={isRTL} />
            ))}
          </div>
        </div>
      )}

      {/* Security sensors */}
      {secSensors.length > 0 && (
        <div className="sh-sec-section">
          <h3 className="sh-section-title">{isRTL ? "أجهزة الاستشعار" : "Sensors"}</h3>
          <div className="sh-sec-grid">
            {secSensors.map(sensor => {
              const cls = sensor.attributes?.device_class || "";
              const on = isEntityOn(sensor);
              const label = cls === "door" || cls === "window" || cls === "opening" || cls === "garage_door"
                ? (on ? (isRTL ? "مفتوح" : "Open") : (isRTL ? "مغلق" : "Closed"))
                : cls === "motion"
                  ? (on ? (isRTL ? "حركة" : "Motion") : (isRTL ? "صافي" : "Clear"))
                  : (on ? (isRTL ? "نشط" : "Active") : (isRTL ? "خامل" : "Inactive"));

              return (
                <div key={sensor.entity_id} className={`sh-sec-card${on ? " sh-sec-alert" : ""}`}>
                  <div className="sh-sec-card-icon">{entityIcon("sensor")}</div>
                  <div className="sh-sec-card-info">
                    <div className="sh-sec-card-name">{sensor.friendly_name}</div>
                    <div className="sh-sec-card-state">{label}</div>
                  </div>
                  <div className={`sh-sec-dot${on ? " sh-sec-dot-alert" : ""}`} />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {locks.length === 0 && alarms.length === 0 && secSensors.length === 0 && (
        <div className="sh-scenes-empty">
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none" stroke="var(--g300)" strokeWidth="1.3">
            <rect x="10" y="16" width="20" height="16" rx="3"/><path d="M14 16v-4a6 6 0 0112 0v4"/><circle cx="20" cy="24" r="2" fill="var(--g300)" stroke="none"/>
          </svg>
          <p>{isRTL ? "لا توجد أجهزة أمان" : "No security devices found."}</p>
        </div>
      )}
    </div>
  );
}

/* ── Alarm card ── */
function AlarmCard({ entity, callService, canControl, isRTL }) {
  const s = entity.state;
  const meta = ALARM_STATES[s] || ALARM_STATES.disarmed;
  const modes = entity.attributes?.supported_features
    ? ["arm_home", "arm_away", "arm_night", "disarm"]
    : ["arm_home", "arm_away", "disarm"];
  const [codeInput, setCodeInput] = useState("");
  const requiresCode = entity.attributes?.code_arm_required || entity.attributes?.code_format;

  const doArm = useCallback((svc) => {
    const data = requiresCode && codeInput ? { code: codeInput } : {};
    callService("alarm_control_panel", svc, entity.entity_id, data);
  }, [callService, entity.entity_id, requiresCode, codeInput]);

  return (
    <div className="sh-alarm-card" style={{ borderColor: meta.color }}>
      <div className="sh-alarm-header">
        <div className="sh-alarm-icon">{entityIcon("alarm")}</div>
        <div>
          <div className="sh-alarm-name">{entity.friendly_name}</div>
          <div className="sh-alarm-state" style={{ color: meta.color, background: meta.bg }}>
            {isRTL ? meta.labelAr : meta.label}
          </div>
        </div>
      </div>
      {canControl && (
        <div className="sh-alarm-controls">
          {requiresCode && (
            <input
              className="fi sh-alarm-code"
              type="password"
              placeholder={isRTL ? "الرمز" : "Code"}
              value={codeInput}
              onChange={e => setCodeInput(e.target.value)}
              dir="ltr"
            />
          )}
          <div className="sh-alarm-btns">
            {s !== "disarmed" && (
              <button className="sh-alarm-btn sh-alarm-disarm" onClick={() => doArm("alarm_disarm")}>
                {isRTL ? "تعطيل" : "Disarm"}
              </button>
            )}
            {s === "disarmed" && modes.includes("arm_home") && (
              <button className="sh-alarm-btn" onClick={() => doArm("alarm_arm_home")}>
                {isRTL ? "تسليح (منزل)" : "Arm Home"}
              </button>
            )}
            {s === "disarmed" && modes.includes("arm_away") && (
              <button className="sh-alarm-btn" onClick={() => doArm("alarm_arm_away")}>
                {isRTL ? "تسليح (خارج)" : "Arm Away"}
              </button>
            )}
            {s === "disarmed" && modes.includes("arm_night") && (
              <button className="sh-alarm-btn" onClick={() => doArm("alarm_arm_night")}>
                {isRTL ? "تسليح (ليلي)" : "Arm Night"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Lock card ── */
function LockCard({ entity, callService, canControl, isRTL }) {
  const locked = entity.state === "locked";
  const [confirming, setConfirming] = useState(false);

  const doAction = useCallback(() => {
    callService("lock", locked ? "unlock" : "lock", entity.entity_id);
    setConfirming(false);
  }, [callService, entity.entity_id, locked]);

  return (
    <div className={`sh-sec-card${!locked ? " sh-sec-alert" : ""}`}>
      <div className="sh-sec-card-icon">{entityIcon("lock")}</div>
      <div className="sh-sec-card-info">
        <div className="sh-sec-card-name">{entity.friendly_name}</div>
        <div className="sh-sec-card-state">{locked ? (isRTL ? "مقفل" : "Locked") : (isRTL ? "مفتوح" : "Unlocked")}</div>
      </div>
      {canControl && (
        <div className="sh-sec-card-action">
          {!confirming ? (
            <button className={`sh-lock-btn${locked ? "" : " sh-lock-warn"}`} onClick={() => setConfirming(true)}>
              {locked ? (isRTL ? "فتح" : "Unlock") : (isRTL ? "قفل" : "Lock")}
            </button>
          ) : (
            <div className="sh-confirm-row">
              <button className="sh-lock-btn sh-lock-confirm" onClick={doAction}>{isRTL ? "تأكيد" : "Confirm"}</button>
              <button className="sh-lock-btn" onClick={() => setConfirming(false)}>{isRTL ? "إلغاء" : "Cancel"}</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SmartHomeSecurity;
